import { useEffect, useRef, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Sidebar from "@ui/Sidebar";
import ChatDrawer from "@features/chat/components/ChatDrawer";
import Dashboard from "@features/dashboard/pages/Dashboard";
import Timeline from "@features/timeline/pages/Timeline";
import Budget from "@features/budget/pages/Budget";
import Contractors from "@features/contractors/pages/Contractors";
import Permits from "@features/permits/pages/Permits";
import Design from "@features/design/pages/Design";
import Feedback from "@features/feedback/pages/Feedback";
import Settings from "@features/settings/pages/Settings";
import ShoppingList from "@features/shopping/pages/ShoppingList";
import Login from "@features/auth/pages/Login";
import PersonaPicker from "@features/onboarding/components/PersonaPicker";
import OnboardingWizard from "@features/onboarding/components/OnboardingWizard";
import useFirestoreDoc from "@data/hooks/useFirestoreDoc";
import { ThemeProvider, useTheme } from "@contexts/ThemeContext";
import { AuthProvider, useAuth } from "@contexts/AuthContext";
import { ProjectProvider, useProjects } from "@contexts/ProjectContext";
import { ChatProvider } from "@contexts/ChatContext";
import {
  defaultPhases,
  defaultBudgetCategories,
  defaultContractors,
} from "@domain/project/projectData";
import { buildPlanB } from "@domain/budget/planB";
import { generateCustomThemeCSS } from "@shared/utils/extractPalette";
import { enforceThemeContrast } from "@shared/utils/enforceThemeContrast";
import TopBar from "./TopBar";
import BottomNav from "./BottomNav";
import DesignTabBar from "./DesignTabBar";
import ChatFAB from "./ChatFAB";
import "./App.css";

const Floorplans = lazy(() => import("@features/floorplans/pages/Floorplans"));
const Invoices = lazy(() => import("@features/invoices/pages/Invoices"));
const defaultPlanBBudget = buildPlanB(defaultBudgetCategories);

function AppContent() {
  const { theme, setTheme } = useTheme();
  const { user, userProfile, profileLoaded, loading, signOut } = useAuth();
  const { projects, loaded: projectsLoaded } = useProjects();
  const [phases, setPhases] = useFirestoreDoc("phases", defaultPhases);
  const [budgetB, setBudgetB] = useFirestoreDoc("budget-b", defaultPlanBBudget);
  const [contractors, setContractors] = useFirestoreDoc("contractors", defaultContractors);
  const [themePref, , themePrefLoaded] = useFirestoreDoc("theme-preference", null, { global: true });
  const [customThemes] = useFirestoreDoc("custom-themes", [], { global: true });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Sync per-user theme preference on login
  useEffect(() => {
    if (themePrefLoaded && themePref && themePref !== theme) {
      setTheme(themePref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themePrefLoaded, themePref]);

  // Resolve which custom theme is active (theme value of "custom:<id>")
  const activeCustomTheme =
    typeof theme === "string" && theme.startsWith("custom:")
      ? (customThemes || []).find((t) => `custom:${t.id}` === theme)
      : null;

  // The data-theme attribute we set on the wrapper. Custom themes all share
  // [data-theme="custom"] CSS rules, but the injected style block is swapped
  // to match whichever custom theme is active.
  const dataTheme = activeCustomTheme ? "custom" : theme;

  // Inject the custom theme CSS whenever the active custom theme changes
  useEffect(() => {
    const id = "custom-theme-style";
    let el = document.getElementById(id);
    if (activeCustomTheme) {
      const css = generateCustomThemeCSS(activeCustomTheme);
      if (!el) {
        el = document.createElement("style");
        el.id = id;
        document.head.appendChild(el);
      }
      el.textContent = css;
    } else if (el) {
      el.remove();
    }
  }, [activeCustomTheme]);

  // After the data-theme attribute / custom CSS is applied, enforce text
  // contrast targets against the actual background. Runs on every theme switch
  // so any text variable that ends up unreadable gets bumped automatically.
  useEffect(() => {
    if (wrapperRef.current) {
      enforceThemeContrast(wrapperRef.current);
    }
  }, [theme, activeCustomTheme]);

  if (loading) {
    return (
      <div
        data-theme={dataTheme}
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--bg-root)" }}
      >
        <p className="text-xs text-gray-500">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div data-theme={dataTheme}>
        <Login />
      </div>
    );
  }

  return (
    <div ref={wrapperRef} data-theme={dataTheme} className="flex min-h-screen" style={{ background: 'var(--bg-root)' }}>
      <Sidebar
        onSignOut={signOut}
        userEmail={user.email}
        customThemes={customThemes}
        mobileOpen={mobileSidebarOpen}
        setMobileOpen={setMobileSidebarOpen}
      />
      {/* overflow-x-clip (not overflow-auto): the window is what scrolls, and
          an overflow container here would silently break position: sticky
          for the TopBar and sticky table headers inside main. */}
      <main className="flex-1 min-w-0 overflow-x-clip relative">
        <TopBar onOpenMenu={() => setMobileSidebarOpen(true)} />

        <Routes>
          <Route
            path="/"
            element={
              <Dashboard
                phases={phases}
                budget={budgetB}
              />
            }
          />
          <Route
            path="/timeline"
            element={<Timeline phases={phases} setPhases={setPhases} />}
          />
          <Route
            path="/budget"
            element={
              <Budget
                budget={budgetB}
                setBudget={setBudgetB}
              />
            }
          />
          <Route
            path="/permits"
            element={<Permits />}
          />
          <Route path="/design" element={<Design />} />
          <Route
            path="/floorplans"
            element={
              <Suspense
                fallback={
                  <div className="p-6 text-xs" style={{ color: "var(--text-secondary)" }}>
                    Loading floorplan viewer…
                  </div>
                }
              >
                <Floorplans />
              </Suspense>
            }
          />
          <Route
            path="/invoices"
            element={
              <Suspense
                fallback={
                  <div className="p-6 text-xs" style={{ color: "var(--text-secondary)" }}>
                    Loading invoices…
                  </div>
                }
              >
                <Invoices budget={budgetB} />
              </Suspense>
            }
          />
          <Route
            path="/shopping"
            element={<ShoppingList budget={budgetB} />}
          />
          <Route
            path="/feedback"
            element={<Feedback contractors={contractors} />}
          />
          <Route
            path="/contractors"
            element={
              <Contractors
                contractors={contractors}
                setContractors={setContractors}
              />
            }
          />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </main>
      <ChatDrawer />
      <BottomNav />
      <DesignTabBar />
      <ChatFAB />
      {profileLoaded && userProfile && userProfile.userType == null && (
        <PersonaPicker />
      )}
      {profileLoaded &&
        userProfile?.userType === "homeowner" &&
        projectsLoaded &&
        projects.length === 0 && <OnboardingWizard />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ProjectProvider>
          <ChatProvider>
            <Router>
              <AppContent />
            </Router>
          </ChatProvider>
        </ProjectProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
